import React, { useState, Children, cloneElement } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { MenuSection, MenuItem } from './types';
interface MenuSectionItemProps {
  section: MenuSection;
  isExpanded: boolean;
  onToggle: (sectionId: string) => void;
  onClose: () => void;
  isReader: boolean;
  darkMode: boolean;
}
export const MenuSectionItem: React.FC<MenuSectionItemProps> = ({
  section,
  isExpanded,
  onToggle,
  onClose,
  isReader,
  darkMode
}) => {
  const [expandedSubGroups, setExpandedSubGroups] = useState<string[]>([]);
  const toggleSubGroup = (e: React.MouseEvent, label: string) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedSubGroups(prev => prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]);
  };
  const hasChildren = section.children && section.children.length > 0;
  const linkClasses = isReader ? darkMode ? 'text-gray-200 hover:bg-gray-700 active:bg-gray-600' : 'text-gray-700 hover:bg-indigo-50 active:bg-indigo-100' : darkMode ? 'text-gray-200 hover:bg-gray-700 active:bg-gray-600' : 'text-gray-700 hover:bg-purple-50 active:bg-purple-100';
  const subMenuClasses = isReader ? darkMode ? 'bg-gray-800 border-indigo-800' : 'bg-gray-50 border-indigo-100' : darkMode ? 'bg-gray-800 border-purple-800' : 'bg-gray-50 border-purple-100';
  const subItemClasses = isReader ? darkMode ? 'text-gray-300 hover:bg-gray-700 active:bg-gray-600' : 'text-gray-600 hover:bg-indigo-100 active:bg-indigo-200' : darkMode ? 'text-gray-300 hover:bg-gray-700 active:bg-gray-600' : 'text-gray-600 hover:bg-purple-100 active:bg-purple-200';
  const nestedSubItemClasses = isReader ? darkMode ? 'text-gray-400 hover:bg-gray-700 active:bg-gray-600' : 'text-gray-500 hover:bg-indigo-100 active:bg-indigo-200' : darkMode ? 'text-gray-400 hover:bg-gray-700 active:bg-gray-600' : 'text-gray-500 hover:bg-purple-100 active:bg-purple-200';
  const renderMenuItem = (item: MenuItem, isNested: boolean = false) => {
    const itemHasChildren = item.children && item.children.length > 0;
    const isSubGroupExpanded = expandedSubGroups.includes(item.label);
    if (itemHasChildren) {
      return <div key={item.label} className="w-full" data-id="element-2338" data-mp="1af4kvgz3snhi">
          <button onClick={e => toggleSubGroup(e, item.label)} className={`flex items-center justify-between w-full px-4 py-3 transition-colors touch-target ${isNested ? nestedSubItemClasses : subItemClasses}`} data-id="element-2339" data-mp="1y4gv8k6631le">
            <div className="flex items-center" data-id="element-2340" data-mp="1o5hxhwmxwqd6">
              {cloneElement(item.icon as React.ReactElement, {
              className: `h-5 w-5 mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`
            })}
              <span className={`text-sm ${isNested ? '' : 'font-medium'}`} data-id="element-2341" data-mp="ct7ak6uvnogn">
                {item.label}
              </span>
            </div>
            {isSubGroupExpanded ? <ChevronDownIcon className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2342" data-mp="2vvvm3vozyyfq" /> : <ChevronRightIcon className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2343" data-mp="34ywq9quayfgr" />}
          </button>

          {isSubGroupExpanded && <div className={`pl-8 border-l-2 ml-4 my-1 ${subMenuClasses}`} data-id="element-2344" data-mp="2d6p82lz5ev09">
              {item.children?.map(child => renderMenuItem(child, true))}
            </div>}
        </div>;
    }
    return <Link key={item.to || item.label} to={item.to || '#'} className={`flex items-center px-4 py-3 transition-colors touch-target ${isNested ? nestedSubItemClasses : subItemClasses}`} onClick={onClose} data-id="element-2345" data-mp="2m0keryfx4ti0">
        {cloneElement(item.icon as React.ReactElement, {
        className: `h-5 w-5 mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`
      })}
        <span className="text-sm" data-id="element-2346" data-mp="227wwv2tt58ba">{item.label}</span>
      </Link>;
  };
  return <div key={section.id} data-id="element-2347" data-mp="2756w1a700ycb">
      {hasChildren ? <button onClick={() => onToggle(section.id)} className={`flex items-center justify-between w-full px-4 py-4 transition-colors touch-target-lg ${linkClasses}`} data-id="element-2348" data-mp="273vtfdpahwms">
          <div className="flex items-center" data-id="element-2349" data-mp="2gxusu4xzb2b1">
            {cloneElement(section.icon as React.ReactElement, {
          className: `h-5 w-5 mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`
        })}
            <span className="text-base font-medium" data-id="element-2350" data-mp="2uvjcxak825f1">{section.label}</span>
          </div>
          {isExpanded ? <ChevronDownIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2351" data-mp="2zudwieq0jy07" /> : <ChevronRightIcon className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2352" data-mp="10j2wxi1mjcwz" />}
        </button> : <Link to={section.to || '#'} className={`flex items-center px-4 py-4 transition-colors touch-target-lg ${linkClasses}`} onClick={onClose} data-id="element-2353" data-mp="xxbzlo2fm9k5">
          {cloneElement(section.icon as React.ReactElement, {
        className: `h-5 w-5 mr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`
      })}
          <span className="text-base font-medium" data-id="element-2354" data-mp="j632yv043nxi">{section.label}</span>
        </Link>}

      {hasChildren && isExpanded && <div className={`pl-4 border-l-2 ml-4 my-1 ${subMenuClasses}`} data-id="element-2355" data-mp="1bx4nzcqhkdoy">
          {section.children?.map(item => renderMenuItem(item))}
        </div>}
    </div>;
};